// src/components/JobView/Tabs/FileArtifactsTab.js
// Archivos v2 — artefactos de prepress versionados con aprobación (PLAN-ARCHIVOS-PREPRESS).
// 7 tipos · versionado append-only · nombre estandarizado por el sistema · aprobación con traza.
import React, { useState, useEffect } from "react";
import { collection, onSnapshot, query, orderBy, getDocs } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import QRCode from "qrcode";
import { CheckCircle2, XCircle, Clock, UploadCloud, History, FileUp, FileJson, Trash2, AlertTriangle } from "lucide-react";
import {
  ARTIFACT_TYPES, ARTIFACT_GROUPS, artifactType, deriveStandardName, extOf,
  createArtifact, addArtifactVersion, setApproval, setArtifactType, deleteArtifact, fixArtifactNames,
} from "../../../lib/artifacts";
import { buildTerminalPackage, buildJobManifest, artifactQrPayload } from "../../../lib/naming";

const APPROVAL_UI = {
  approved: { label: "Aprobado", cls: "bg-green-100 text-green-700", Icon: CheckCircle2 },
  rejected: { label: "Rechazado", cls: "bg-red-100 text-red-700", Icon: XCircle },
  pending: { label: "Pendiente", cls: "bg-amber-100 text-amber-700", Icon: Clock },
};

const FileArtifactsTab = ({ jobId, jobData, db, storage, user }) => {
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newType, setNewType] = useState("input");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [versions, setVersions] = useState({}); // artifactId -> [versions]

  useEffect(() => {
    const q = query(collection(db, `jobs/${jobId}/artifacts`), orderBy("createdAt", "asc"));
    const unsub = onSnapshot(q, (snap) => {
      setArtifacts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, [db, jobId]);

  const uploadToStorage = async (file, artifactId, standardName) => {
    const storageRef = ref(storage, `jobs/${jobId}/artifacts/${artifactId}/${standardName}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  // Subir un artefacto NUEVO (versión 1).
  const handleNewArtifact = async (file) => {
    if (!file) return;
    setBusy(true); setError("");
    try {
      // seq = orden dentro de su tipo (para que el nombre no se repita si hay varios).
      const seq = artifacts.filter((a) => a.type === newType).length + 1;
      const artifactId = await createArtifact(db, jobId, { type: newType, seq, user });
      const standardName = deriveStandardName(jobData, newType, 1, extOf(file.name), seq);
      const url = await uploadToStorage(file, artifactId, standardName);
      await addArtifactVersion(db, jobId, artifactId, {
        url, fileName: standardName, originalName: file.name,
        size: file.size, contentType: file.type, previousVersion: 0, user,
      });
    } catch (e) { setError("No se pudo subir: " + e.message); }
    finally { setBusy(false); }
  };

  // Subir una NUEVA VERSIÓN a un artefacto existente.
  const handleNewVersion = async (art, file) => {
    if (!file) return;
    setBusy(true); setError("");
    try {
      const standardName = deriveStandardName(jobData, art.type, (art.currentVersion || 0) + 1, extOf(file.name), art.seq);
      const url = await uploadToStorage(file, art.id, standardName);
      await addArtifactVersion(db, jobId, art.id, {
        url, fileName: standardName, originalName: file.name,
        size: file.size, contentType: file.type, previousVersion: art.currentVersion || 0, user,
      });
    } catch (e) { setError("No se pudo subir la versión: " + e.message); }
    finally { setBusy(false); }
  };

  // Cambiar el TIPO (categoría) de un artefacto ya subido. Re-deriva el nombre estándar
  // para que el código de tipo coincida; el archivo (URL) se conserva.
  const handleChangeType = async (art, nextType) => {
    if (!nextType || nextType === art.type) return;
    setBusy(true); setError("");
    try {
      const ext = extOf(art.latestName || "") || "dat";
      const latestName = art.currentVersion ? deriveStandardName(jobData, nextType, art.currentVersion, ext, art.seq) : "";
      await setArtifactType(db, jobId, art.id, { type: nextType, latestName });
    } catch (e) { setError("No se pudo cambiar el tipo: " + e.message); }
    finally { setBusy(false); }
  };

  const handleDelete = async (art) => {
    if (!window.confirm(`¿Borrar el archivo «${art.latestName || art.title || "este artefacto"}»? No se puede deshacer.`)) return;
    try { await deleteArtifact(db, jobId, art.id); }
    catch (e) { setError("No se pudo borrar: " + e.message); }
  };

  const handleApprove = (art) => setApproval(db, jobId, art.id, { status: "approved", user }).catch((e) => setError(e.message));
  const handleReject = (art) => {
    const reason = window.prompt("Motivo del rechazo:");
    if (reason === null) return;
    setApproval(db, jobId, art.id, { status: "rejected", user, reason }).catch((e) => setError(e.message));
  };

  const toggleHistory = async (art) => {
    if (expanded === art.id) { setExpanded(null); return; }
    setExpanded(art.id);
    if (!versions[art.id]) {
      const snap = await getDocs(query(collection(db, `jobs/${jobId}/artifacts/${art.id}/versions`), orderBy("version", "desc")));
      setVersions((prev) => ({ ...prev, [art.id]: snap.docs.map((d) => d.data()) }));
    }
  };

  const downloadJson = (obj, filename) => {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Paquete JSON para la terminal (estilo SheetModal): el script de Illustrator
  // lo consume para renombrar y colocar el QR según el estándar del sistema.
  const handleTerminalJson = async (art) => {
    try {
      const qrCodeBase64 = await QRCode.toDataURL(artifactQrPayload({ jobId, artifactId: art.id, type: art.type, version: art.currentVersion }));
      const pkg = buildTerminalPackage({ id: jobId, jobNumber: jobData?.jobNumber, jobName: jobData?.jobName }, art, { qrCodeBase64 });
      downloadJson(pkg, `${art.latestName || art.id}.json`);
    } catch (e) { setError("No se pudo generar el JSON de terminal: " + e.message); }
  };

  // ¿Hay nombres estándar repetidos? (datos viejos creados antes del `seq`).
  const hasDupeNames = (() => {
    const seen = new Set();
    for (const a of artifacts) {
      const n = a.latestName;
      if (!n) continue;
      if (seen.has(n)) return true;
      seen.add(n);
    }
    return false;
  })();

  const handleFixNames = async () => {
    setBusy(true); setError("");
    try {
      const n = await fixArtifactNames(db, jobId, jobData, artifacts);
      if (!n) setError("");
    } catch (e) { setError("No se pudieron corregir los nombres: " + e.message); }
    finally { setBusy(false); }
  };

  const handleManifest = () => {
    downloadJson(buildJobManifest({ id: jobId, jobName: jobData?.jobName }, artifacts), `${jobId}_manifiesto.json`);
  };

  const byType = (key) => artifacts.filter((a) => a.type === key);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-800">Archivos</h2>
        <p className="text-sm text-gray-500">Prepress (dieline, arte, imposición…) y documentos (manuales, presentaciones, renders) — versionado + aprobación con traza. El sistema asigna el nombre estándar.</p>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>}

      {/* Subir nuevo artefacto */}
      <div className="flex flex-wrap items-center gap-2 p-3 bg-gray-50 rounded-lg border">
        <UploadCloud size={18} className="text-gray-500" />
        <select value={newType} onChange={(e) => setNewType(e.target.value)} className="p-2 border border-gray-300 rounded-md text-sm">
          {ARTIFACT_GROUPS.map((g) => (
            <optgroup key={g} label={g}>
              {ARTIFACT_TYPES.filter((t) => t.group === g).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </optgroup>
          ))}
        </select>
        <label className={`px-3 py-2 rounded-md text-sm font-semibold cursor-pointer ${busy ? "bg-gray-200 text-gray-400" : "bg-blue-600 text-white hover:bg-blue-700"}`}>
          {busy ? "Subiendo…" : "Subir archivo"}
          <input type="file" className="hidden" disabled={busy} onChange={(e) => { handleNewArtifact(e.target.files?.[0]); e.target.value = ""; }} />
        </label>
        {hasDupeNames && (
          <button type="button" onClick={handleFixNames} disabled={busy} className="ml-auto inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-semibold bg-amber-100 text-amber-800 hover:bg-amber-200 disabled:opacity-40" title="Hay archivos con el mismo nombre. Renumera por tipo para que cada uno sea único.">
            <AlertTriangle size={16} /> Corregir nombres duplicados
          </button>
        )}
        <button type="button" onClick={handleManifest} disabled={artifacts.length === 0} className={`${hasDupeNames ? "" : "ml-auto"} inline-flex items-center gap-1 px-3 py-2 rounded-md text-sm font-semibold bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed`} title="Descarga el manifiesto de nombres estándar del trabajo">
          <FileJson size={16} /> Manifiesto
        </button>
      </div>

      {loading ? <p className="text-gray-400 text-center py-8">Cargando…</p> : (
        <div className="space-y-6">
          {ARTIFACT_TYPES.map((t) => {
            const items = byType(t.key);
            if (items.length === 0) return null;
            return (
              <div key={t.key}>
                <h3 className="text-sm font-bold text-gray-600 uppercase mb-2">{t.label}</h3>
                <div className="space-y-2">
                  {items.map((art) => {
                    const ap = APPROVAL_UI[art.approval?.status || "pending"];
                    return (
                      <div key={art.id} className="border rounded-lg p-3 bg-white">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="min-w-0">
                            <a href={art.latestUrl} target="_blank" rel="noreferrer" className="font-mono text-sm font-semibold text-blue-600 hover:underline break-all">{art.latestName || "(sin archivo)"}</a>
                            <span className="ml-2 text-xs text-gray-400">v{art.currentVersion}</span>
                            {art.latestOriginalName && art.latestOriginalName !== art.latestName && (
                              <p className="text-[11px] text-gray-400">subiste «{art.latestOriginalName}» → renombrado por el sistema</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {/* Cambiar el TIPO del archivo (re-categorizar). */}
                            <select value={art.type} onChange={(e) => handleChangeType(art, e.target.value)} disabled={busy} title="Cambiar tipo de archivo" className="text-xs border border-gray-300 rounded p-1 max-w-[160px] text-gray-600">
                              {ARTIFACT_GROUPS.map((g) => (
                                <optgroup key={g} label={g}>
                                  {ARTIFACT_TYPES.filter((t) => t.group === g).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                                </optgroup>
                              ))}
                            </select>
                            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${ap.cls}`}>
                              <ap.Icon size={13} /> {ap.label}
                            </span>
                            <button onClick={() => handleApprove(art)} title="Aprobar" className="text-gray-400 hover:text-green-600"><CheckCircle2 size={18} /></button>
                            <button onClick={() => handleReject(art)} title="Rechazar" className="text-gray-400 hover:text-red-600"><XCircle size={18} /></button>
                            <label title="Sustituir archivo (sube una nueva versión)" className="text-gray-400 hover:text-blue-600 cursor-pointer"><FileUp size={18} /><input type="file" className="hidden" onChange={(e) => { handleNewVersion(art, e.target.files?.[0]); e.target.value = ""; }} /></label>
                            {artifactType(art.type)?.group === "Prepress" && (
                              <button onClick={() => handleTerminalJson(art)} title="JSON para terminal (Illustrator)" className="text-gray-400 hover:text-emerald-600"><FileJson size={18} /></button>
                            )}
                            <button onClick={() => toggleHistory(art)} title="Historial" className="text-gray-400 hover:text-gray-700"><History size={18} /></button>
                            <button onClick={() => handleDelete(art)} title="Borrar archivo" className="text-gray-400 hover:text-red-600"><Trash2 size={18} /></button>
                          </div>
                        </div>
                        {art.approval?.status === "rejected" && art.approval?.reason && (
                          <p className="text-xs text-red-600 mt-1">Rechazo: {art.approval.reason} — {art.approval.by}</p>
                        )}
                        {art.approval?.status === "approved" && (
                          <p className="text-xs text-green-600 mt-1">Aprobado por {art.approval.by}</p>
                        )}
                        {expanded === art.id && (
                          <div className="mt-2 border-t pt-2 space-y-1">
                            {(versions[art.id] || []).map((v) => (
                              <div key={v.version} className="flex items-center justify-between text-xs text-gray-500">
                                <a href={v.url} target="_blank" rel="noreferrer" className="font-mono text-blue-600 hover:underline">{v.fileName}</a>
                                <span>v{v.version} · {v.uploadedByName} {v.originalName ? `· (${v.originalName})` : ""}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {artifacts.length === 0 && <p className="text-gray-400 text-center py-6">Aún no hay artefactos versionados. Sube el primero arriba.</p>}

          {/* Documentos heredados (subidos antes de Archivos v2: manuales, presentaciones, renders) */}
          {(jobData?.documents || []).filter((d) => d && (d.url || d.name)).length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-600 uppercase mb-2">Documentos (heredados)</h3>
              <div className="space-y-2">
                {(jobData.documents || []).filter((d) => d && (d.url || d.name)).map((d, i) => (
                  <div key={i} className="border rounded-lg p-3 bg-gray-50 flex items-center justify-between gap-2">
                    <a href={d.url} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline break-all">{d.name || "(documento)"}</a>
                    <span className="text-xs text-gray-400 shrink-0">{d.category || d.description || "heredado"}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">Subidos antes de Archivos v2. Vuelve a subirlos arriba (como Manual/Presentación/Render…) para versionarlos y aprobarlos.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FileArtifactsTab;
